import { BrandMark, cn } from '@dnx/ui';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { ChevronDown, LogOut, Menu, Monitor, Moon, Sun, UserRound, X } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { initials } from '../lib/labels.js';
import { getTheme, setTheme, type Theme } from '../lib/theme.js';

/**
 * Casca do painel e do console: moldura de papel com a navegação e um "palco" escuro, arredondado,
 * onde o trabalho acontece. Páginas de altura cheia (conversas, funil, agenda) ocupam o palco inteiro.
 */
export function StageShell({ sidebar, topbar, fullBleed, section, banner }: {
  sidebar: ReactNode; topbar: ReactNode; fullBleed?: boolean; section?: { group: string; page: string };
  /** faixa fixa sob o topo (ex.: alerta urgente do pós-operatório) */
  banner?: ReactNode;
}) {
  const [mobileNav, setMobileNav] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => setMobileNav(false), [path]);

  return (
    <div className="min-h-dvh bg-bg text-fg md:flex md:h-dvh md:overflow-hidden">
      <aside className="hidden w-[268px] shrink-0 flex-col px-4 pt-6 pb-5 md:flex">
        <div className="min-h-0 flex-1">{sidebar}</div>
        <ThemeSwitch />
      </aside>
      {mobileNav && (
        // no celular a navegação abre sobre o palco, fora dele (herda o papel, não a tinta)
        <div className="fixed inset-x-0 top-16 bottom-0 z-40 overflow-y-auto border-t border-border bg-bg px-4 pt-4 pb-6 md:hidden">
          {sidebar}
          <ThemeSwitch />
        </div>
      )}

      <div className="dnx-stage dnx-stage-texture flex min-h-dvh min-w-0 flex-1 flex-col md:my-3 md:mr-3 md:min-h-0 md:overflow-hidden md:rounded-[26px] md:border md:border-border md:shadow-[0_24px_70px_-36px_oklch(0.2_0.02_255/0.35)]">
        <header className="relative z-20 flex h-16 shrink-0 items-center gap-3 border-b border-border px-4 sm:px-8">
          <button type="button" className="grid size-9 place-items-center rounded-lg text-fg-muted hover:bg-surface-muted hover:text-fg md:hidden"
            aria-label={mobileNav ? 'Fechar menu' : 'Abrir menu'} aria-expanded={mobileNav} onClick={() => setMobileNav((v) => !v)}>
            {mobileNav ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
          <span className="md:hidden"><BrandMark compact /></span>
          {section && (
            <p className="hidden items-center gap-3 text-[11px] font-semibold tracking-[0.22em] text-fg-subtle uppercase md:flex">
              <span aria-hidden className="h-px w-7 bg-fg-subtle/60" />{section.group}
              <span aria-hidden className="text-fg-subtle/50">/</span><span className="text-fg-muted">{section.page}</span>
            </p>
          )}
          <div className="ml-auto flex items-center gap-2">{topbar}</div>
        </header>
        {banner}

        <main className={cn('min-h-0 flex-1', fullBleed ? 'flex flex-col overflow-hidden' : 'overflow-y-auto px-5 py-9 sm:px-10 sm:py-12 lg:px-14')}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Item da navegação lateral (no papel). */
export function SideLink({ to, exact, Icon, label, badge }: {
  to: string; exact?: boolean; Icon: React.ComponentType<{ className?: string }>; label: string; badge?: ReactNode;
}) {
  return (
    <Link to={to} activeOptions={{ exact, includeSearch: false }}
      className="group flex h-10 items-center gap-3.5 rounded-xl px-3 text-[14.5px] font-medium text-fg-muted transition-colors hover:bg-[oklch(0.2_0.02_85/0.05)] hover:text-fg"
      activeProps={{ className: '!bg-surface !text-fg font-semibold shadow-card ring-1 ring-border [&_svg]:text-primary' }}>
      <Icon className="size-[19px] shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {badge}
    </Link>
  );
}

export function SideGroup({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className="mt-6 first:mt-0">
      {label && <p className="mb-2 px-3 text-[10.5px] font-semibold tracking-[0.2em] text-fg-subtle uppercase">{label}</p>}
      <ul className="flex flex-col gap-1">{children}</ul>
    </div>
  );
}

/** Menu do usuário no canto do palco: avatar, nome, papel; abre "Meu perfil" e "Sair". */
export function UserMenu({ name, subtitle, onLogout, profileTo }: {
  name: string; subtitle: string; onLogout: () => void; profileTo: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-haspopup="menu"
        className="flex items-center gap-3 rounded-full py-1 pr-2 pl-1 hover:bg-surface-muted">
        <span className="grid size-9 place-items-center rounded-full bg-fg text-[12px] font-bold text-bg">{initials(name)}</span>
        <span className="hidden min-w-0 text-left sm:block">
          <span className="block max-w-[220px] truncate text-[14px] leading-tight font-semibold text-fg">{name}</span>
          <span className="block max-w-[220px] truncate text-[12.5px] leading-tight text-fg-muted">{subtitle}</span>
        </span>
        <ChevronDown className={cn('size-4 text-fg-subtle transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-56 overflow-hidden rounded-xl border border-border bg-surface p-1.5 shadow-pop">
          <Link to={profileTo} role="menuitem" onClick={() => setOpen(false)}
            className="flex h-10 items-center gap-2.5 rounded-lg px-3 text-[14px] text-fg hover:bg-surface-muted">
            <UserRound className="size-4 text-fg-subtle" />Meu perfil
          </Link>
          <button type="button" role="menuitem" onClick={onLogout}
            className="flex h-10 w-full items-center gap-2.5 rounded-lg px-3 text-left text-[14px] text-fg hover:bg-surface-muted">
            <LogOut className="size-4 text-fg-subtle" />Sair
          </button>
        </div>
      )}
    </div>
  );
}

/** Tema no rodapé da lateral: Sistema, Claro ou Escuro (fica salvo neste navegador). */
export function ThemeSwitch() {
  const [theme, setThemeState] = useState<Theme>(getTheme);
  const choose = (t: Theme) => { setTheme(t); setThemeState(t); };
  return (
    <div className="mt-5 grid grid-cols-3 gap-1 rounded-xl border border-border bg-surface/60 p-1" role="radiogroup" aria-label="Tema">
      {([['system', 'Sistema', Monitor], ['light', 'Claro', Sun], ['dark', 'Escuro', Moon]] as const).map(([t, label, Icon]) => (
        <button key={t} type="button" role="radio" aria-checked={theme === t} onClick={() => choose(t)} title={`Tema: ${label}`}
          className={cn('flex h-8 items-center justify-center gap-1.5 rounded-lg text-[12px] font-medium transition-colors',
            theme === t ? 'bg-surface text-fg shadow-card ring-1 ring-border' : 'text-fg-muted hover:text-fg')}>
          <Icon className="size-3.5" aria-hidden />{label}
        </button>
      ))}
    </div>
  );
}
